import React, { useEffect, useMemo, useRef, useState } from "react";

import { formatCmOutcome, getUserFacingApiErrorMessage, productAnalytics, useMutateSoilSightDetailedAnalysis, useTranslation } from "utils";

import { CURRENCY_CODE, CurrencyCode } from "consts";

import { SoilSightDetailedAnalysis, SoilSightKeyInsight } from "models";

import { AntModal, Button, Flex, Icon, Option, Select, Skeleton, Typography } from "components";

import { ScenarioData } from "pages/app/scenario-analysis/types/scenario";
import { SoilSightOutcomesByScenario } from "pages/app/scenario-analysis/utils/forecast";
import { getScenarioCumulativeCm, getScenariosWithProjections } from "pages/app/scenario-analysis/utils/keyInsightsHelpers";

export interface KeyInsightsModalProps {
  open: boolean;
  onClose: () => void;
  scenarios: ScenarioData[];
  soilSightOutcomes: SoilSightOutcomesByScenario | null;
  projectionYears: number;
  /** Soil Sight project id (passed as `projectId` to the detailed-analysis endpoint). */
  projectId: string | null;
  /** Key insight from the most recent `/soil-sight/run`. Drives the default selector value. */
  keyInsight: SoilSightKeyInsight | null;
  /** Currency from the most recent `/soil-sight/run`. Falls back to EUR for older responses. */
  currency: CurrencyCode | null;
  preselectedScenarioId?: string | null;
}

export const KeyInsightsModal = ({
  open,
  onClose,
  scenarios,
  soilSightOutcomes,
  projectionYears,
  projectId,
  keyInsight,
  currency,
  preselectedScenarioId = null,
}: KeyInsightsModalProps) => {
  const { t, i18n } = useTranslation("scenarioAnalysis");

  const scenariosWithProjections = useMemo(() => getScenariosWithProjections(scenarios, soilSightOutcomes), [scenarios, soilSightOutcomes]);

  const defaultSelectedScenarioId = useMemo(() => {
    if (preselectedScenarioId && scenariosWithProjections.some((oneScenario) => oneScenario.id === preselectedScenarioId)) {
      return preselectedScenarioId;
    }
    if (keyInsight?.bestScenarioId && scenariosWithProjections.some((oneScenario) => oneScenario.id === keyInsight.bestScenarioId)) {
      return keyInsight.bestScenarioId;
    }
    return scenariosWithProjections[0]?.id ?? null;
  }, [preselectedScenarioId, keyInsight, scenariosWithProjections]);

  const [selectedScenarioId, setSelectedScenarioId] = useState<string | null>(defaultSelectedScenarioId);
  const [analysis, setAnalysis] = useState<SoilSightDetailedAnalysis | null>(null);
  const [bestScenarioIdFromResponse, setBestScenarioIdFromResponse] = useState<string | null>(keyInsight?.bestScenarioId ?? null);
  const [analysisError, setAnalysisError] = useState<{ error: unknown } | null>(null);

  const detailedAnalysisMutation = useMutateSoilSightDetailedAnalysis();
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchAnalysisForScenario = (targetScenarioId: string) => {
    if (!projectId) return;
    abortControllerRef.current?.abort();
    const newAbortController = new AbortController();
    abortControllerRef.current = newAbortController;
    setAnalysisError(null);

    detailedAnalysisMutation.mutate(
      { projectId, scenarioId: targetScenarioId, signal: newAbortController.signal },
      {
        onSuccess: (analysisResponse) => {
          productAnalytics.track("soilsights:key_insights_view", {
            is_reference: Boolean(scenarios.find((scenario) => scenario.id === targetScenarioId)?.isReference),
          });
          setAnalysis(analysisResponse.analysis);
          setBestScenarioIdFromResponse(analysisResponse.bestScenarioId);
        },
        onError: (mutationError) => {
          if ((mutationError as { name?: string })?.name === "CanceledError" || (mutationError as { name?: string })?.name === "AbortError") return;
          setAnalysisError({ error: mutationError });
        },
      }
    );
  };

  useEffect(() => {
    if (!open) {
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
      setAnalysis(null);
      setAnalysisError(null);
      setBestScenarioIdFromResponse(null);
      setSelectedScenarioId(null);
      detailedAnalysisMutation.reset();
      return;
    }
    if (!selectedScenarioId && defaultSelectedScenarioId) {
      setSelectedScenarioId(defaultSelectedScenarioId);
    }
    // Mount-time fetch
    const targetScenarioId = selectedScenarioId ?? defaultSelectedScenarioId;
    if (targetScenarioId) {
      fetchAnalysisForScenario(targetScenarioId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch on (re)open only; selector changes fetch via handleScenarioChange
  }, [open]);

  const previousLanguageRef = useRef<string | null>(null);
  useEffect(() => {
    if (previousLanguageRef.current === null) {
      previousLanguageRef.current = i18n.language;
      return;
    }
    if (previousLanguageRef.current === i18n.language) return;
    previousLanguageRef.current = i18n.language;
    if (!open) return;
    if (!selectedScenarioId) return;
    fetchAnalysisForScenario(selectedScenarioId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch only when the language changes, for the scenario already shown
  }, [i18n.language]);

  const handleScenarioChange = (nextScenarioId: string) => {
    setSelectedScenarioId(nextScenarioId);
    fetchAnalysisForScenario(nextScenarioId);
  };

  const handleRetry = () => {
    if (selectedScenarioId) fetchAnalysisForScenario(selectedScenarioId);
  };

  const handleClose = () => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    onClose();
  };

  const bestScenarioIdForCard = bestScenarioIdFromResponse ?? keyInsight?.bestScenarioId ?? null;
  const bestScenario = scenarios.find((oneScenario) => oneScenario.id === bestScenarioIdForCard) ?? null;
  const bestScenarioCumulativeMargin = bestScenario ? getScenarioCumulativeCm(bestScenario.id, soilSightOutcomes, projectionYears) : 0;
  const currencyForFormatting = currency ?? CURRENCY_CODE.EUR;

  const isLoadingFirstAnalysis = detailedAnalysisMutation.isPending;

  const isSingleScenarioOption = scenariosWithProjections.length <= 1;

  return (
    <AntModal
      open={open}
      onCancel={handleClose}
      footer={null}
      width={720}
      centered
      destroyOnClose
      closeIcon={<Icon iconName="tabler/x" size="m" />}
      className="key-insights-modal"
      title={
        <Flex align="center" gap="0.5rem" className="key-insights-modal-header">
          <Icon iconName="klim/plant-ai" size="m" />
          <Typography variant="h3">{t("keyInsights.modal.title")}</Typography>
        </Flex>
      }
    >
      <div className="key-insights-modal-body">
        <div className="key-insights-modal-selector-row">
          <Select
            value={selectedScenarioId ?? undefined}
            onChange={(nextScenarioId: string) => handleScenarioChange(nextScenarioId)}
            disabled={isSingleScenarioOption}
            className="key-insights-modal-scenario-select"
            popupMatchSelectWidth={false}
            virtual={false}
            dataTestId="key-insights-modal-scenario-select"
            aria-labelledby="key-insights-modal-scenario-select-label"
          >
            {scenariosWithProjections.map((oneScenario) => {
              const baselineSuffix = oneScenario.isReference ? ` ${t("keyInsights.modal.scenarioBaselineSuffix")}` : "";
              return (
                <Option key={oneScenario.id} value={oneScenario.id}>
                  {`${oneScenario.title}${baselineSuffix}`}
                </Option>
              );
            })}
          </Select>
        </div>

        {bestScenario && (
          <section className="key-insights-modal-best-card" aria-labelledby="key-insights-modal-best-label" data-testid="key-insights-modal-best-card">
            <div className="key-insights-modal-best-row">
              <span className="key-insights-modal-best-icon" aria-hidden>
                <Icon iconName="klim/medal" size="l" className="key-insights-modal-best-icon-graphic" />
              </span>
              <div className="key-insights-modal-best-copy">
                <Typography variant="paragraph-m" id="key-insights-modal-best-label" className="key-insights-modal-best-label">
                  {t("keyInsights.modal.bestContributionMarginLabel")}
                </Typography>
                <Typography variant="h4" className="key-insights-modal-best-summary" data-testid="key-insights-modal-best-summary">
                  {t("keyInsights.modal.bestContributionMarginSummary", {
                    scenarioName: bestScenario.title,
                    amount: formatCmOutcome(bestScenarioCumulativeMargin, { currency: currencyForFormatting }),
                    years: projectionYears,
                  })}
                </Typography>
              </div>
            </div>
          </section>
        )}

        {analysisError !== null && !analysis ? (
          <div className="key-insights-modal-error" data-testid="key-insights-modal-error">
            <Typography variant="paragraph-m" color="grey-600" className="key-insights-modal-error-body">
              {getUserFacingApiErrorMessage(analysisError.error, t("keyInsights.modal.errorBody"))}
            </Typography>
            <Button type="primary" size="middle" onClick={handleRetry} className="key-insights-modal-retry">
              {t("keyInsights.modal.retry")}
            </Button>
          </div>
        ) : isLoadingFirstAnalysis ? (
          <div className="key-insights-modal-sections" aria-busy data-testid="key-insights-modal-loading">
            {[0, 1, 2, 3].map((sectionIndex) => (
              <div key={sectionIndex} className="key-insights-modal-section">
                <Skeleton type="title" width="40%" />
                <Skeleton type="paragraph" rows={3} />
              </div>
            ))}
          </div>
        ) : analysis ? (
          <div className="key-insights-modal-sections">
            <section className="key-insights-modal-section" aria-labelledby="key-insights-modal-section-cultivation">
              <Typography variant="paragraph-l" id="key-insights-modal-section-cultivation" className="key-insights-modal-section-title">
                {t("keyInsights.modal.sectionCultivation")}
              </Typography>
              <Typography variant="paragraph-s" color="grey-600" className="key-insights-modal-section-body">
                {analysis.cultivation}
              </Typography>
            </section>

            <section className="key-insights-modal-section" aria-labelledby="key-insights-modal-section-financial">
              <Typography variant="paragraph-l" id="key-insights-modal-section-financial" className="key-insights-modal-section-title">
                {t("keyInsights.modal.sectionFinancial")}
              </Typography>
              <Typography variant="paragraph-s" color="grey-600" className="key-insights-modal-section-body">
                {analysis.financial}
              </Typography>
            </section>

            <section className="key-insights-modal-section" aria-labelledby="key-insights-modal-section-implementation">
              <Typography variant="paragraph-l" id="key-insights-modal-section-implementation" className="key-insights-modal-section-title">
                {t("keyInsights.modal.sectionImplementation")}
              </Typography>
              <Typography variant="paragraph-s" color="grey-600" className="key-insights-modal-section-body">
                {analysis.implementation}
              </Typography>
            </section>

            <section className="key-insights-modal-section" aria-labelledby="key-insights-modal-section-risks-opps">
              <Typography variant="paragraph-l" id="key-insights-modal-section-risks-opps" className="key-insights-modal-section-title">
                {t("keyInsights.modal.sectionRiskOpportunities")}
              </Typography>
              <ul className="key-insights-modal-bullets" role="list">
                {[...analysis.risks, ...analysis.opportunities].map((oneBullet, bulletIndex) => (
                  <li key={`${bulletIndex}-${oneBullet}`}>
                    <Typography variant="paragraph-s" color="grey-600">
                      {oneBullet}
                    </Typography>
                  </li>
                ))}
              </ul>
            </section>
          </div>
        ) : null}

        <section className="key-insights-modal-assumptions" aria-labelledby="key-insights-modal-assumptions-label">
          <div className="key-insights-modal-assumptions-row">
            <span className="key-insights-modal-assumptions-icon" aria-hidden>
              <Icon iconName="tabler/info-circle" size="s" className="key-insights-modal-assumptions-icon-graphic" />
            </span>
            <div className="key-insights-modal-assumptions-content">
              <Typography variant="paragraph-s" bold id="key-insights-modal-assumptions-label" className="key-insights-modal-assumptions-title">
                {t("keyInsights.modal.modelAssumptionsTitle")}
              </Typography>
              <div className="key-insights-modal-assumptions-body">
                <Typography variant="paragraph-s" className="key-insights-modal-assumptions-line">
                  {t("keyInsights.modal.modelAssumptionsBodyLine1")}
                </Typography>
                <Typography variant="paragraph-s" className="key-insights-modal-assumptions-line">
                  {t("keyInsights.modal.modelAssumptionsBodyLine2")}
                </Typography>
              </div>
            </div>
          </div>
        </section>
      </div>
    </AntModal>
  );
};

export default KeyInsightsModal;
